<template>
  <div class="relative w-full">
    <div
      ref="searchInput"
      class="w-full rounded-lg lg:grow flex items-center justify-start text-neutral-600 bg-white h-12 border border-neutral-300"
    >
      <div class="flex flex-row items-center justify-start space-x-2 w-full pl-4">
        <fa
          :icon="['fas', 'search']"
          class="text-xl text-ocean-700 pointer-events-none"
        />
        <input
          v-model="query"
          class="flex-1 border-0 text-base rounded-r-md focus:outline-none focus:ring-2 focus:ring-ocean-400 hover:ring-2 hover:ring-ocean-200 pl-2 h-[46px] text-neutral-900 placeholder-text-neutral-500"
          placeholder="What is your pet's health concern?"
        />
        <fa
          v-if="loading"
          :icon="['fal', 'circle-notch']"
          class="absolute right-4 text-xl text-ocean-700 pointer-events-none fa-spin"
        />
      </div>
    </div>

    <div
      ref="searchResult"
      class="rounded-lg absolute w-full top-12 mt-1 z-999"
    >
      <transition name="fade-fast">
        <div
          v-if="hasResults"
          class="bg-white border border-neutral-300 max-h-80 scroll-pt-11 space-y-2 overflow-y-auto shadow-2xl rounded-lg"
        >
          <ul class="my-2 text-sm text-neutral-800">
            <li
              v-for="(item, i) in results"
              :key="`item-${i}`"
              class="cursor-pointer select-none px-4 py-2 hover:bg-ocean-100 hover:text-ocean-900"
              @click="handleClick(item)"
              @keydown.enter="handleClick(item)"
              v-html="titleHtml(item)"
            />
          </ul>
        </div>
        <div
          v-else-if="!loading && queryCompleted !== ''"
          class="bg-white border border-neutral-300 px-6 py-12 sm:px-12 shadow-2xl rounded-lg"
        >
          <fa
            :icon="['fal', 'face-sad-tear']"
            class="text-4xl text-ocean-700 pointer-events-none"
          />
          <p class="font-title text-xl mt-4 font-semibold text-neutral-900">
            {{ $t('search.empty-results') }}
          </p>
          <p class="mt-2 text-neutral-600">
            {{ $t('search.we-couldnt-find-anything') }}
          </p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleNotch,
  faFaceSadTear,
  faSearch,
} from '@fortawesome/pro-light-svg-icons';
import debounce from 'lodash/debounce';

import ClickAway from '@/mixins/ClickAway';

library.add({
  faCircleNotch,
  faFaceSadTear,
  faSearch,
});

export default {
  name: 'VetsterEntitySearchV2',

  mixins: [ClickAway],

  props: {
    isGlossary: {
      type: Boolean,
      default: false,
    },
    isCondition: {
      type: Boolean,
      default: false,
    },
    isSymptom: {
      type: Boolean,
      default: false,
    },
    species: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      controller: null,
      loading: false,
      results: [],
      queryCompleted: '',
      query: '',
    };
  },

  computed: {
    hasResults() {
      return this.results.length > 0;
    },
    getPayload() {
      return {
        filter: { query: this.query },
        per_page: 5,
        // symptom: this.isSymptom,
        // condition: this.isCondition,
        // species: this.species,
        // glossary: this.isGlossary,
      };
    },
  },

  watch: {
    query() {
      this.fetchResults();
    },
  },

  created() {
    this.$bus.$on('escape', this.handleEscape);
  },

  mounted() {
    document.addEventListener('click', this.registerClickAway);
  },

  methods: {
    handleEscape() {
      this.query = '';
      this.results = [];
      this.loading = false;
    },
    handleClick(item) {
      window.location.href = item.url;
    },
    fetchResults: debounce(async function search() {
      this.controller?.abort();
      this.queryCompleted = '';
      if (this.query === '') {
        this.results = [];

        return;
      }

      this.controller = new AbortController();
      const { signal } = this.controller;
      const payload = this.getPayload;
      this.loading = true;

      await this.$api.get('p/posts/search', { params: payload }, { signal })
        .then((response) => {
          this.results = response.data.data;
          this.queryCompleted = payload.query;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
          this.controller = null;
        });
    }, 250, {
      maxWait: 750,
    }),
    registerClickAway(event) {
      this.hideOnClickAway(
        event,
        this.$refs.searchResult,
        this.handleEscape,
        this.$refs.searchInput,
      );
    },
    titleHtml(item) {
      return item.highlights?.title || item.title;
    },
  },
};
</script>
